/**
 * Hook genérico de transcripción en tiempo real.
 *
 * Orquesta AudioCaptureService + TranscriptionWebSocket para
 * llamadas Telnyx WebRTC.
 *
 * Uso:
 *   const tx = useLiveTranscription();
 *   // Cuando la llamada se activa:
 *   await tx.startCapture(callId, remoteStream);  // MediaStream del peer
 *   // o con HTMLAudioElement:
 *   await tx.startCapture(callId, null, audioElement);
 *   // Al colgar:
 *   tx.stopCapture();
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { config } from '@/config/constants';
import { AudioCaptureService } from '@/services/audioCapture/AudioCaptureService';
import { TranscriptionWebSocket } from '@/services/transcriptionWebSocket';
import TokenStorage from '@/utils/tokenStorage';
import type {
  TranscriptionSegment,
  TranscriptionWSStatus,
} from '@/types/transcription';

export interface LiveTranscriptionState {
  isCapturing: boolean;
  wsStatus: TranscriptionWSStatus;
  segments: TranscriptionSegment[];
  error: string | null;
}

export interface UseLiveTranscriptionReturn {
  state: LiveTranscriptionState;
  /** Inicia captura de audio y transcripción WS */
  startCapture: (
    callId: string,
    remoteStream?: MediaStream | null,
    remoteAudioElement?: HTMLAudioElement | null,
  ) => Promise<void>;
  /**
   * Agrega un MediaStream remoto a la captura en curso.
   * Útil cuando el stream del cliente llega después del estado 'active' (timing race).
   * Idempotente: ignora si ya se agregó este stream.
   */
  addRemoteStream: (stream: MediaStream) => Promise<void>;
  /** Detiene captura y desconecta WS */
  stopCapture: () => void;
  /** Limpia todos los segmentos */
  clearSegments: () => void;
}

const MAX_SEGMENTS = 500;

export function useLiveTranscription(): UseLiveTranscriptionReturn {
  const [isCapturing, setIsCapturing] = useState(false);
  const [wsStatus, setWsStatus] = useState<TranscriptionWSStatus>('idle');
  const [segments, setSegments] = useState<TranscriptionSegment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const captureRef = useRef<AudioCaptureService | null>(null);
  const wsRef = useRef<TranscriptionWebSocket | null>(null);
  const cleanupFnsRef = useRef<Array<() => void>>([]);
  /** Si true, un startCapture en curso debe abortar (ej. llamada colgada antes de que capture.start() termine) */
  const cancelledRef = useRef(false);
  /** ID del MediaStream remoto ya conectado — evita agregar el mismo stream dos veces */
  const addedRemoteStreamIdRef = useRef<string | null>(null);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      stopCaptureInternal();
    };
  }, []);

  const stopCaptureInternal = useCallback(() => {
    cancelledRef.current = true;
    addedRemoteStreamIdRef.current = null;

    for (const fn of cleanupFnsRef.current) {
      fn();
    }
    cleanupFnsRef.current = [];

    if (captureRef.current) {
      captureRef.current.stop();
      captureRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.disconnect();
      wsRef.current = null;
    }

    setIsCapturing(false);
    setWsStatus('idle');
  }, []);

  const startCapture = useCallback(
    async (
      callId: string,
      remoteStream?: MediaStream | null,
      remoteAudioElement?: HTMLAudioElement | null,
    ) => {
      stopCaptureInternal();
      setError(null);
      cancelledRef.current = false;

      if (!AudioCaptureService.isSupported()) {
        const msg =
          'El navegador no soporta captura de audio (AudioWorklet/getUserMedia)';
        setError(msg);
        console.error(`[useLiveTranscription] ❌ ${msg}`);
        return;
      }

      try {
        console.warn('[useLiveTranscription] 🚀 Iniciando transcripción para call:', callId);
        console.warn('[useLiveTranscription] remoteStream:', remoteStream ? `active=${remoteStream.active}, tracks=${remoteStream.getAudioTracks().length}` : 'null');
        console.warn('[useLiveTranscription] remoteAudioElement:', remoteAudioElement ? 'sí' : 'null');

        // 1. WebSocket de transcripción
        const ws = new TranscriptionWebSocket(callId);
        wsRef.current = ws;

        const unsubStatus = ws.onStatusChange((status) => {
          console.warn('[useLiveTranscription] 🔄 WS status:', status);
          setWsStatus(status);
        });
        cleanupFnsRef.current.push(unsubStatus);

        const unsubTranscription = ws.onTranscription((segment) => {
          console.warn('[useLiveTranscription] 📝 Segmento:', segment.channel, segment.isFinal ? '(final)' : '(parcial)', segment.text.slice(0, 50));
          setSegments((prev) => {
            // Reemplazar interinos del mismo canal, mantener finales
            const withoutInterim = prev.filter(
              (s) => s.isFinal || s.channel !== segment.channel,
            );
            const next = [...withoutInterim, segment];
            return next.length > MAX_SEGMENTS
              ? next.slice(-MAX_SEGMENTS)
              : next;
          });
        });
        cleanupFnsRef.current.push(unsubTranscription);

        console.warn('[useLiveTranscription] 🔌 Conectando WebSocket...');
        const connected = await ws.connect();
        if (!connected) {
          const apiKeyOnly =
            !TokenStorage.getAccessToken() && !!config.API_BEARER_KEY;
          setError(
            apiKeyOnly
              ? 'La transcripción en vivo requiere iniciar sesión con usuario; la API key del CRM no aplica al WebSocket.'
              : 'No hay sesión válida para la transcripción en vivo. Inicia sesión de nuevo.',
          );
          stopCaptureInternal();
          return;
        }

        // 2. Captura de audio (await puede tardar: carga worklet + getUserMedia)
        console.warn('[useLiveTranscription] 🎙️ Iniciando AudioCaptureService...');
        const capture = new AudioCaptureService();
        captureRef.current = capture;

        capture.onAudioChunk((chunk, channel) => {
          ws.sendAudioChunk(chunk, channel);
        });

        await capture.start(remoteAudioElement ?? null);
        if (cancelledRef.current) {
          console.warn('[useLiveTranscription] ⏹️ Abortado tras capture.start() (llamada ya terminada)');
          stopCaptureInternal();
          return;
        }

        // 3. Si hay MediaStream remoto (ej. TelnyxRTC), agregarlo directamente
        if (remoteStream) {
          if (!remoteStream.active) {
            console.warn('[useLiveTranscription] ⚠️ remoteStream ya inactivo, omitiendo addRemoteStream');
          } else {
            console.warn('[useLiveTranscription] 📞 Agregando remoteStream...');
            await capture.addRemoteStream(remoteStream);
            addedRemoteStreamIdRef.current = remoteStream.id;
          }
        }
        if (cancelledRef.current) {
          console.warn('[useLiveTranscription] ⏹️ Abortado tras addRemoteStream (llamada ya terminada)');
          stopCaptureInternal();
          return;
        }

        setIsCapturing(true);
        console.warn(
          `[useLiveTranscription] ✅ Captura iniciada para call ${callId}`,
        );
      } catch (err: any) {
        if (cancelledRef.current) return;
        const msg = err?.message ?? 'Error iniciando captura de audio';
        setError(msg);
        console.error('[useLiveTranscription] ❌ Error:', err);
        stopCaptureInternal();
      }
    },
    [stopCaptureInternal],
  );

  const addRemoteStream = useCallback(async (stream: MediaStream) => {
    if (!captureRef.current) {
      console.warn('[useLiveTranscription] addRemoteStream: no hay captura activa');
      return;
    }
    if (addedRemoteStreamIdRef.current === stream.id) {
      console.warn('[useLiveTranscription] addRemoteStream: stream ya conectado, omitiendo');
      return;
    }
    if (!stream.active) {
      console.warn('[useLiveTranscription] addRemoteStream: stream inactivo, omitiendo');
      return;
    }
    try {
      console.warn('[useLiveTranscription] 📞 addRemoteStream tardío (timing fix):', stream.id);
      await captureRef.current.addRemoteStream(stream);
      addedRemoteStreamIdRef.current = stream.id;
      console.warn('[useLiveTranscription] ✅ Stream remoto tardío conectado');
    } catch (err) {
      console.error('[useLiveTranscription] ❌ Error en addRemoteStream tardío:', err);
    }
  }, []);

  const stopCapture = useCallback(() => {
    stopCaptureInternal();
    logger.info('[useLiveTranscription] Captura detenida');
  }, [stopCaptureInternal]);

  const clearSegments = useCallback(() => {
    setSegments([]);
  }, []);

  return {
    state: { isCapturing, wsStatus, segments, error },
    startCapture,
    addRemoteStream,
    stopCapture,
    clearSegments,
  };
}
